import React, { useState, useEffect } from 'react';
import { fetchPastBishops } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  History, 
  Quote, 
  Award,
  BookOpen,
  ShieldCheck,
  MoveRight
} from 'lucide-react';

const BASE_URL = `https://api.ibadansouthanglicandiocese.org.ng/`;

const PastBishops = () => {
  const [pastBishops, setPastBishops] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadPastBishops = async () => {
      try {
        const response = await fetchPastBishops();
        setPastBishops(response.data.bishops);
      } catch (error) {
        console.error('Error fetching past bishops:', error);
        toast({ title: "Registry Update", description: "Displaying available historical records." });
      } finally {
        setLoading(false);
      }
    };
    loadPastBishops();
  }, [toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="relative">
            <History className="h-12 w-12 text-amber-700 animate-spin-slow" />
            <div className="absolute inset-0 h-12 w-12 border-t-2 border-amber-900 rounded-full animate-spin" />
          </div>
          <p className="mt-6 text-slate-500 font-serif tracking-widest text-xs uppercase italic">Consulting the Archives</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2] py-20">
      <div className="max-w-6xl mx-auto px-6">
        
        {/* Archival Header */}
        <header className="mb-32 relative text-center">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-block px-4 py-1.5 border border-amber-200 rounded-full mb-6"
          >
            <span className="text-[10px] uppercase tracking-[0.5em] text-amber-700 font-bold">Historical Register</span>
          </motion.div>
          <h1 className="text-6xl md:text-8xl font-serif font-black text-slate-900 tracking-tight mb-8">
            The <span className="text-amber-700">Lineage</span>
          </h1>
          <div className="flex items-center justify-center gap-6 text-slate-400">
            <div className="h-px w-16 bg-slate-200" />
            <p className="font-serif italic text-lg max-w-lg">
              Honoring the shepherds who guided the Diocese of Ibadan South through the changing tides of time.
            </p>
            <div className="h-px w-16 bg-slate-200" />
          </div>
        </header>

        {/* Ledger Grid */}
        <div className="space-y-40">
          {pastBishops
            .sort((a, b) => b.enthronement_date - a.enthronement_date)
            .map((bishop, idx) => (
              <motion.section
                key={bishop.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                className={`relative flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-16`}
              >
                {/* Visual Side */}
                <div className="w-full lg:w-5/12 relative">
                  <div className="relative aspect-[4/5] z-10 group overflow-hidden rounded-2xl shadow-2xl bg-white p-3">
                    <img
                      src={bishop.photo.startsWith('http') ? bishop.photo : `${BASE_URL}${bishop.photo}`}
                      alt={bishop.name}
                      className="w-full h-full object-cover rounded-xl grayscale group-hover:grayscale-0 transition-all duration-1000"
                    />
                    {/* Floating Tenure Badge */}
                    <div className="absolute -bottom-6 -right-6 bg-slate-900 text-white p-8 rounded-full h-32 w-32 flex flex-col items-center justify-center border-8 border-[#F9F7F2] z-20">
                      <span className="text-[10px] uppercase font-bold text-amber-500">Enthroned</span>
                      <span className="text-xl font-serif font-bold">{bishop.enthronement_date}</span>
                    </div>
                  </div>
                  {/* Background Decal */}
                  <div className={`absolute -top-10 ${idx % 2 === 0 ? '-left-10' : '-right-10'} text-slate-100 font-black text-9xl -z-0 pointer-events-none select-none`}>
                    0{idx + 1}
                  </div>
                </div>

                {/* Content Side */}
                <div className="w-full lg:w-7/12 space-y-8">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="h-5 w-5 text-amber-600" />
                      <span className="h-px w-12 bg-amber-200" />
                      <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{bishop.qualifications}</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 leading-tight">
                      {bishop.title} <br />
                      <span className="text-amber-800">{bishop.name}</span>
                    </h2>
                  </div>

                  <div className="relative">
                    <Quote className="absolute -left-10 -top-4 h-12 w-12 text-amber-100 -z-10" />
                    <p className="text-xl text-slate-600 font-serif leading-relaxed italic">
                      "{bishop.bio}"
                    </p>
                  </div>

                  {bishop.achievements && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-8 border-t border-slate-200">
                      <div>
                        <h4 className="flex items-center gap-2 text-xs font-black uppercase text-slate-900 mb-4 tracking-widest">
                          <Award className="h-4 w-4 text-amber-600" />
                          Ministries & Legacy
                        </h4>
                        <ul className="space-y-3">
                          {bishop.achievements.map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-slate-500 group">
                              <MoveRight className="h-4 w-4 text-amber-300 group-hover:text-amber-600 transition-colors mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm self-start">
                        <BookOpen className="h-5 w-5 text-amber-600 mb-3" />
                        <p className="text-xs text-slate-400 leading-relaxed uppercase font-bold tracking-tighter">
                          Archival Note:
                        </p>
                        <p className="text-sm text-slate-600 font-serif mt-2">
                          Records from this tenure are preserved in the Diocesan Registry for historical study.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.section>
            ))}
        </div>

        {/* Empty State */}
        {pastBishops.length === 0 && (
          <div className="text-center py-40 border-2 border-dashed border-slate-200 rounded-3xl">
            <History className="h-16 w-16 text-slate-200 mx-auto mb-6" />
            <h3 className="text-2xl font-serif text-slate-400 uppercase tracking-widest">Archive Pending</h3>
            <p className="text-slate-400 mt-2">Historical documents are being prepared for public display.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PastBishops;